import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  MiniThreadMatch,
  GetMiniThreadsParams,
  GetMiniThreadsResponse,
} from '@dreamai/types';
import ThreadViewContainer from '../components/chat/sidebar/ThreadViewContainer';
import { AgentProvider } from './AgentContext';
import { ReactComponent as CloseIcon } from '../icons/linked-threads/close.svg';
import { ReactComponent as MinimizeIcon } from '../icons/linked-threads/minimize.svg';
import { ReactComponent as EmbiggenIcon } from '../icons/linked-threads/embiggen.svg';
import { useThreadLoading } from './ThreadLoadingContext';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';

interface OpenThread {
  id: string; // unique identifier for this open thread instance
  threadId: string | null; // actual thread ID (null for new threads)
  componentName: string;
  match: MiniThreadMatch;
  position: number; // 0 = rightmost, 1 = second from right, etc.
  description?: string; // thread description loaded from API
  minimized?: boolean; // whether the thread is minimized
  agentId?: string; // agent ID for creating new threads
}

// Type for mini-thread tracking
interface MiniThreadInfo {
  threadIds: string[];
  loading: boolean;
}

interface LinkedThreadsContextType {
  openThreads: OpenThread[];
  openThread: (
    threadId: string | null,
    componentName: string,
    match: MiniThreadMatch,
    agentId?: string,
  ) => void;
  closeThread: (id: string) => void;
  getOpenThreads: () => OpenThread[];
  updateThreadDescription: (openThreadId: string, description: string) => void;
  toggleMinimizeThread: (id: string) => void;
  // Mini-threads tracking
  getMiniThreadInfo: (
    componentName: string,
    match: MiniThreadMatch,
  ) => MiniThreadInfo;
}

const LinkedThreadsContext = createContext<
  LinkedThreadsContextType | undefined
>(undefined);

export const useLinkedThreads = () => {
  const context = useContext(LinkedThreadsContext);
  if (!context) {
    throw new Error(
      'useLinkedThreads must be used within a LinkedThreadsProvider',
    );
  }
  return context;
};

interface LinkedThreadsProviderProps {
  children: React.ReactNode;
}

export const LinkedThreadsProvider: React.FC<LinkedThreadsProviderProps> = ({
  children,
}) => {
  const [openThreads, setOpenThreads] = useState<OpenThread[]>([]);
  const [miniThreadsCache, setMiniThreadsCache] = useState<
    Record<string, MiniThreadInfo>
  >({});
  const { setThreadLoading, setThreadAborting } = useThreadLoading();
  const { getAccessTokenSilently } = useAuth0();

  const openThread = useCallback(
    (
      threadId: string | null,
      componentName: string,
      match: MiniThreadMatch,
      agentId?: string,
    ) => {
      // Check if this thread is already open
      const existingThread = openThreads.find(
        (thread) =>
          thread.threadId === threadId &&
          thread.componentName === componentName &&
          JSON.stringify(thread.match) === JSON.stringify(match),
      );

      if (existingThread) {
        // Thread already open, don't duplicate
        return;
      }

      // Create new thread instance
      const newThread: OpenThread = {
        id: `thread-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        threadId,
        componentName,
        match,
        position: 0, // New threads start at position 0 (rightmost)
        agentId, // Store agentId for new thread creation
      };

      // Add new thread at the beginning and reposition existing threads
      setOpenThreads((prev) => {
        const updatedExisting = prev.map((thread) => ({
          ...thread,
          position: thread.position + 1, // Push existing threads to the left
        }));
        return [newThread, ...updatedExisting];
      });
    },
    [openThreads],
  );

  const closeThread = useCallback(
    (id: string) => {
      setOpenThreads((prev) => {
        // Find the thread being closed to clean up its loading state
        const threadToClose = prev.find((thread) => thread.id === id);

        if (threadToClose) {
          // Clean up loading and aborting states for the thread being closed
          // For new threads (threadId is null), this will clean up the NEW_THREAD state
          setThreadLoading(threadToClose.threadId, false);
          setThreadAborting(threadToClose.threadId, false);
        }

        const filtered = prev.filter((thread) => thread.id !== id);
        // Reposition remaining threads
        return filtered.map((thread, index) => ({
          ...thread,
          position: index,
        }));
      });
    },
    [setThreadLoading, setThreadAborting],
  );

  const getOpenThreads: () => OpenThread[] = useCallback(
    (): OpenThread[] => openThreads,
    [openThreads],
  );

  const updateThreadDescription = useCallback(
    (openThreadId: string, description: string) => {
      setOpenThreads((prev) =>
        prev.map((thread) =>
          thread.id === openThreadId ? { ...thread, description } : thread,
        ),
      );
    },
    [],
  );

  const toggleMinimizeThread = useCallback((id: string) => {
    setOpenThreads((prev) =>
      prev.map((thread) =>
        thread.id === id ? { ...thread, minimized: !thread.minimized } : thread,
      ),
    );
  }, []);

  // Helper function to create cache key
  const createCacheKey = useCallback(
    (componentName: string, match: MiniThreadMatch) => {
      return `${componentName}-${JSON.stringify(match)}`;
    },
    [],
  );

  // Get mini-thread info - fetches if not cached
  const getMiniThreadInfo = useCallback(
    (componentName: string, match: MiniThreadMatch): MiniThreadInfo => {
      const cacheKey = createCacheKey(componentName, match);
      const cached = miniThreadsCache[cacheKey];

      if (cached) {
        return cached;
      }

      // If not cached, start fetching and return loading state
      const fetchMiniThreads = async () => {
        try {
          const token = await getAccessTokenSilently();
          const params: GetMiniThreadsParams = {
            location: componentName,
            match: match,
          };

          const response = await api.get<GetMiniThreadsResponse>(
            '/agents/mini-threads',
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: {
                location: params.location,
                match: params.match ? JSON.stringify(params.match) : undefined,
              },
            },
          );

          if (response.data.success && response.data.miniThreads) {
            const threads = response.data.miniThreads;
            const threadIds = threads.map(
              (thread: { id: string }) => thread.id,
            );

            setMiniThreadsCache((prev) => ({
              ...prev,
              [cacheKey]: {
                threadIds,
                loading: false,
              },
            }));
          } else {
            setMiniThreadsCache((prev) => ({
              ...prev,
              [cacheKey]: {
                threadIds: [],
                loading: false,
              },
            }));
          }
        } catch (error) {
          console.error('Error fetching mini-threads:', error);
          setMiniThreadsCache((prev) => ({
            ...prev,
            [cacheKey]: {
              threadIds: [],
              loading: false,
            },
          }));
        }
      };

      // Set loading state and start fetch
      setMiniThreadsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          threadIds: [],
          loading: true,
        },
      }));

      fetchMiniThreads();

      // Return loading state immediately
      return {
        threadIds: [],
        loading: true,
      };
    },
    [miniThreadsCache, createCacheKey, getAccessTokenSilently],
  );

  // Handle thread creation completion
  const handleThreadCreated = useCallback(
    (threadId: string, agentId: string, openThreadId: string) => {
      console.log('LTC Debug: ', threadId, agentId, openThreadId);
      setOpenThreads((prev) =>
        prev.map((thread) => {
          console.log('LTC Debug: ', thread.id, openThreadId);
          if (thread.id === openThreadId) {
            // Optimistically update mini-threads cache
            const cacheKey = createCacheKey(thread.componentName, thread.match);
            setMiniThreadsCache((cacheState) => {
              const existing = cacheState[cacheKey];
              if (existing) {
                // Add new threadId if not already present
                const updatedThreadIds = existing.threadIds.includes(threadId)
                  ? existing.threadIds
                  : [...existing.threadIds, threadId];
                return {
                  ...cacheState,
                  [cacheKey]: {
                    ...existing,
                    threadIds: updatedThreadIds,
                  },
                };
              }
              // Create new cache entry
              return {
                ...cacheState,
                [cacheKey]: {
                  threadIds: [threadId],
                  loading: false,
                },
              };
            });
            return { ...thread, threadId };
          }
          return thread;
        }),
      );
    },
    [createCacheKey],
  );

  const contextValue: LinkedThreadsContextType = {
    openThreads,
    openThread,
    closeThread,
    getOpenThreads,
    updateThreadDescription,
    toggleMinimizeThread,
    getMiniThreadInfo,
  };

  // Calculate responsive positioning for threads
  const [dimensions, setDimensions] = useState(() => {
    if (typeof window !== 'undefined') {
      const isMobile = window.innerWidth <= 768;
      return {
        threadWidth: isMobile ? Math.min(window.innerWidth - 32, 400) : 580,
        threadHeight: isMobile ? Math.min(window.innerHeight - 120, 500) : 600,
        threadMaxHeight: isMobile
          ? Math.min(window.innerHeight - 120, 500)
          : 600,
        minimizedThreadHeight: 58,
        threadSpacing: isMobile ? 16 : 20,
        bottomMargin: isMobile ? 16 : 8,
        rightMargin: isMobile ? 16 : 8,
        isMobile,
      };
    }
    return {
      threadWidth: 580,
      threadHeight: 600,
      threadMaxHeight: 600,
      minimizedThreadHeight: 46,
      threadSpacing: 20,
      bottomMargin: 20,
      rightMargin: 20,
      isMobile: false,
    };
  });

  // Update dimensions on window resize
  React.useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      setDimensions({
        threadWidth: isMobile ? Math.min(window.innerWidth - 32, 400) : 580,
        threadHeight: isMobile ? Math.min(window.innerHeight - 120, 500) : 600,
        threadMaxHeight: isMobile
          ? Math.min(window.innerHeight - 120, 500)
          : 600,
        minimizedThreadHeight: 58,
        threadSpacing: isMobile ? 16 : 20,
        bottomMargin: isMobile ? 16 : 8,
        rightMargin: isMobile ? 16 : 8,
        isMobile,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <LinkedThreadsContext.Provider value={contextValue}>
      {children}

      {/* Render open threads with responsive positioning */}
      {openThreads.map((thread, index) => {
        // Calculate positioning based on screen size
        let positioning: React.CSSProperties;
        let heightStyles: React.CSSProperties;

        // Determine if this is a fast-chat thread for smaller dimensions
        const isFastChat = thread.componentName === 'fast-chat';
        const threadWidth = isFastChat ? 480 : dimensions.threadWidth;
        const threadHeight = isFastChat ? 480 : dimensions.threadHeight;

        if (dimensions.isMobile) {
          // On mobile: stack vertically from bottom with flexible height
          positioning = {
            bottom: `${dimensions.bottomMargin + index * (dimensions.minimizedThreadHeight + dimensions.threadSpacing)}px`,
            right: `${dimensions.rightMargin}px`,
            left: `${dimensions.rightMargin}px`,
            width: 'auto',
          };

          // Use flexible height on mobile
          heightStyles = thread.minimized
            ? { height: `${dimensions.minimizedThreadHeight}px` }
            : { maxHeight: `${dimensions.threadMaxHeight}px`, height: 'auto' };
        } else {
          // On desktop: stack horizontally from right with fixed height
          const rightOffset =
            dimensions.rightMargin +
            index * (threadWidth + dimensions.threadSpacing);
          positioning = {
            bottom: `${dimensions.bottomMargin}px`,
            right: `${rightOffset}px`,
            width: `${threadWidth}px`,
          };

          // Use fixed height on desktop
          const currentHeight = thread.minimized
            ? dimensions.minimizedThreadHeight
            : threadHeight;
          heightStyles = { height: `${currentHeight}px` };
        }

        return (
          <div
            key={thread.id}
            className={`linked-thread-container ${thread.minimized ? 'minimized' : ''} ${isFastChat ? 'fast-chat' : ''}`}
            style={{
              ...positioning,
              ...heightStyles,
              zIndex: 1060 + index,
            }}
          >
            {/* Thread header */}
            <div
              className='linked-thread-header'
              onClick={
                thread.minimized
                  ? () => toggleMinimizeThread(thread.id)
                  : undefined
              }
              style={{ cursor: thread.minimized ? 'pointer' : 'default' }}
            >
              <span>
                {thread.componentName === 'fast-chat'
                  ? 'Quick chat'
                  : thread.description
                    ? thread.description
                    : thread.threadId
                      ? 'Thread'
                      : 'New Thread'}
              </span>
              <div className='linked-thread-controls'>
                {thread.componentName !== 'fast-chat' && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `/conversations?agent_id=${thread.agentId}&thread_id=${thread.threadId}`;
                      }}
                      className='linked-thread-embiggen'
                    >
                      <EmbiggenIcon />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMinimizeThread(thread.id);
                      }}
                      className='linked-thread-minimize'
                    >
                      <MinimizeIcon />
                    </button>
                  </>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeThread(thread.id);
                  }}
                  className='linked-thread-close'
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Thread content - keep mounted but hide when minimized */}
            <div
              className='linked-thread-content'
              style={{
                display: thread.minimized ? 'none' : 'flex',
                height: thread.minimized ? '0' : 'auto',
                overflow: 'hidden',
                flex: 1,
              }}
            >
              <AgentProvider>
                <ThreadViewContainer
                  threadId={thread.threadId}
                  inMiniMode={true}
                  agentId={thread.agentId}
                  creationMode={thread.threadId === null}
                  discussingObject={thread.componentName}
                  discussingObjectMatch={thread.match}
                  onThreadCreated={(threadId, agentId) =>
                    handleThreadCreated(threadId, agentId, thread.id)
                  }
                  onDescriptionChange={(description) =>
                    updateThreadDescription(thread.id, description)
                  }
                />
              </AgentProvider>
            </div>
          </div>
        );
      })}
    </LinkedThreadsContext.Provider>
  );
};
